'use client'

import LoadingIndicator from '@/components/LoadingIndicator'
import { BASKET_COLUMN_WIDTH } from '@/components/page-specific/catering/supplier-menu/components/basketColumnWidth'
import { useMenuCardColumns } from '@/components/page-specific/catering/supplier-menu/components/menu-items-renderer/useMenuCardColumns'
import { SidebarFiltersSkeleton } from '@/components/page-specific/catering/supplier-menu/components/skeleton/SidebarFiltersSkeleton'
import { SidebarShell } from '@/components/ui/SidebarShell'
import { Box, Chip, Container, Grid, Skeleton, Stack } from '@mui/material'

import { useEffect, useState } from 'react'

export function SidebarAndMenuSkeleton() {
  const [messageStage, setMessageStage] = useState<0 | 1 | 2>(0)
  const { ref: menuRef, cardCols } = useMenuCardColumns()

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setMessageStage(1)
    }, 5000)

    const timer2 = setTimeout(() => {
      setMessageStage(2)
    }, 10000)
    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
    }
  }, [])

  return (
    <>
      <Box sx={{ gridArea: 'sidebar', display: { xs: 'none', lg: 'block' } }}>
        <SidebarShell ariaLabel="Supplier menu filters" width={280} stickyOffset={0}>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <SidebarFiltersSkeleton />
          </Box>
        </SidebarShell>
      </Box>

      <Box
        component="main"
        sx={{
          gridArea: 'content',
          p: { xs: 2, md: 2 },
        }}>
        <Container disableGutters sx={{ maxWidth: { xs: 'lg', xl: 'xl' }, ml: 0 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 'grow' }} sx={{ minWidth: 0 }}>
              <Stack ref={menuRef} spacing={2} sx={{ position: 'relative' }}>
                {messageStage > 0 && (
                  <Chip
                    icon={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          ml: 0.5,
                          mt: '2px',
                          '& img': {
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          },
                        }}>
                        <LoadingIndicator size="small" />
                      </Box>
                    }
                    label={
                      <span>
                        Worth the wait...
                        {messageStage === 2 && <strong> this menu is stacked</strong>}
                      </span>
                    }
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      py: 2.5,
                      px: 1,
                      '& .MuiChip-icon': {
                        ml: 1,
                      },
                    }}
                  />
                )}
                {[1, 2, 3].map((i) => (
                  <Box key={i}>
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, mb: 1 }} />
                    <Grid container spacing={1.5}>
                      {[1, 2, 3, 4].map((j) => (
                        <Grid size={12 / cardCols} key={j}>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              border: (t) => t.borders?.light || '1px solid #eee',
                              borderRadius: 2,
                              p: 2,
                            }}>
                            <Skeleton
                              variant="rectangular"
                              width={120}
                              height={100}
                              sx={{ borderRadius: 2, flexShrink: 0 }}
                            />
                            <Box sx={{ flex: 1, width: '100%' }}>
                              <Skeleton variant="text" width="70%" height={32} />
                              <Skeleton variant="text" width="60%" />
                              <Skeleton variant="text" width="40%" />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 'auto' }} sx={{ width: BASKET_COLUMN_WIDTH }}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  )
}
